'use client';

import React from 'react';

import { cn } from '../helpers/cn';

import ImageWithTransition from './ImageWithTransition';

type BackdropAndPosterCompositeImageProps = {
    title?: string;
    backdropImage?: string;
    backdropBlurDataImage?: string;
    posterImage?: string;
    posterBlurDataImage?: string;
    showPoster?: boolean;
    className?: string;
    children?: React.ReactNode;
    /** Passed through to both the backdrop and poster `next/image` elements to enable responsive image loading. */
    sizes?: string;
    /** Whether the poster image is eagerly loaded with `priority`. Defaults to `true` to preserve existing behavior. */
    posterPriority?: boolean;
};

export function BackdropAndPosterCompositeImage({
    title,
    backdropImage,
    backdropBlurDataImage,
    posterImage,
    posterBlurDataImage,
    showPoster,
    className,
    children,
    sizes,
    posterPriority = true
}: BackdropAndPosterCompositeImageProps) {
    return (
        <div className={cn('bg-black rounded-8 overflow-hidden', className)}>
            <div className="relative w-full h-full">
                <div className="relative w-full h-full">
                    {backdropImage && (
                        <ImageWithTransition
                            className="w-full h-full object-cover"
                            alt={title ? `${title} Backdrop` : 'event-backdrop-image'}
                            src={backdropImage}
                            placeholder={backdropBlurDataImage ? 'blur' : 'empty'}
                            blurDataURL={backdropBlurDataImage}
                            width={1000}
                            height={1000}
                            sizes={sizes}
                            priority
                        />
                    )}

                    <div
                        className="absolute w-full h-full top-0 left-0 p-16 lg:p-18 flex flex-row items-center"
                        style={
                            showPoster && posterImage
                                ? {
                                      background:
                                          'linear-gradient(to right, rgba(0, 0, 0, 0.7) 0 25%, rgba(0, 0, 0, 0) 50%)'
                                  }
                                : undefined
                        }>
                        {showPoster && posterImage && (
                            <ImageWithTransition
                                className={cn('w-auto h-full aspect-[2/3] rounded-4 object-cover')}
                                alt={title ? `${title} Poster` : 'event-poster-image'}
                                src={posterImage}
                                placeholder={posterBlurDataImage ? 'blur' : 'empty'}
                                blurDataURL={posterBlurDataImage}
                                width={1000}
                                height={1000}
                                sizes={sizes}
                                priority={posterPriority}
                            />
                        )}
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}
